import { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import FeedbackCollector from '@atlaskit/feedback-collector';

import urls from 'src/components/navigation/src/urls';
import { showFlagComponent } from 'src/redux/flags';
import { toggleFeedbackButton } from 'src/redux/global/actions/toggle-feedback-button';
import fetchUserEmails from 'src/sections/repository/actions/fetch-user-emails';
import {
  getCurrentUserEmail,
  getIsFeedbackModalOpen,
} from 'src/selectors/global-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { urls as workspaceUrls } from 'src/urls/workspaces';

import { messages } from './navigation-feedback-collector.i18n';

type Props = {
  isLoggedIn: boolean;
  userDisplayName?: string;
  userAccountId?: string;
};

export const BaseNavigationFeedbackCollector = (props: Props) => {
  const { isLoggedIn, userDisplayName, userAccountId } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const [isShowFlag, setIsShowFlag] = useState(false);

  const entrypointId = '3e0d44da-9d2b-43b6-9119-e17b876ee1b4';

  const isFeedbackModalOpen = useSelector(getIsFeedbackModalOpen);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchUserEmails());
    }

    if (isShowFlag) {
      dispatch(showFlagComponent('horizontal-nav-feedback-button'));
    }
  }, [dispatch, isLoggedIn, isShowFlag]);

  const currentUserEmail = useSelector(getCurrentUserEmail);

  return (
    <>
      {isFeedbackModalOpen && (
        <FeedbackCollector
          locale={intl.locale}
          entrypointId={entrypointId}
          atlassianAccountId={userAccountId}
          name={userDisplayName}
          email={currentUserEmail || undefined}
          onClose={() => {
            dispatch(toggleFeedbackButton(false));
          }}
          onSubmit={() => setIsShowFlag(true)}
          additionalFields={[
            {
              // "Feedback Context" custom field
              id: 'customfield_10047',
              value: `URL: ${window.location.href}\nUser Agent: ${window.navigator.userAgent}`,
            },
          ]}
          showTypeField={false}
          feedbackTitle={intl.formatMessage(messages.giveFeedbackButton)}
          feedbackTitleDetails={
            <>
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsOne}
                tagName="p"
              />
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsTwo}
                values={{
                  link: chunks => (
                    <a href={workspaceUrls.external.contactSupportLink}>
                      {chunks}
                    </a>
                  ),
                }}
                tagName="p"
              />
            </>
          }
          canBeContactedLabel={
            <FormattedMessage
              {...messages.canContactLabel}
              values={{
                link: chunks => (
                  <a href={urls.external.privacyPolicy()}>{chunks}</a>
                ),
              }}
              tagName="p"
            />
          }
          enrolInResearchLabel={
            <FormattedMessage
              {...messages.enrolLabel}
              values={{
                link: chunks => (
                  <a href={urls.external.atlassianResearchGroup()}>{chunks}</a>
                ),
              }}
              tagName="p"
            />
          }
          summaryPlaceholder={intl.formatMessage(
            messages.giveFeedbackPlaceholder
          )}
        />
      )}
    </>
  );
};

export const NavigationFeedbackCollector = () => {
  const currentUser = useSelector(getCurrentUser);

  return (
    <BaseNavigationFeedbackCollector
      isLoggedIn={!!currentUser?.uuid}
      userDisplayName={currentUser?.display_name}
      userAccountId={currentUser?.account_id}
    />
  );
};
